import * as AuthHelper from '@Services/Auth/AuthHelper';
import { FEATURES } from '@Constants/rx-ui/constants';
import { RCS10D, RCSANY } from '@Pages/RetroSynthesis/ProjectParametersDialog/constants';
import uuid from 'uuid/v4';
import {
  isRCSChecked as isRCSCheckedFn,
  isRPSAvailable as isRPSAvailableFn,
  mapUsedRCSToRPSBuildingBlockLibrariesIktos,
  mapUsedRCSToRPSBuildingBlockLibrariesPai,
} from '@Pages/RetroSynthesis/ProjectParametersDialog/RPS/mapRCSToRPS';
import { ProjectSynthesisPartner } from './constants';

export const isNewSynthesisPlannerEnabled = () => !AuthHelper.isRMCLicense();

export const isPaiPredictiveRetrosynthesisEnabled = () => AuthHelper.isFeatureEnabled(FEATURES.pending_ai_rt);

export const isIktosPredictiveRetrosynthesisEnabled = () => AuthHelper.isFeatureEnabled(FEATURES.iktos_rt);

export const isAnyPredictiveRetrosynthesisAvailable = () =>
  isPaiPredictiveRetrosynthesisEnabled() || isIktosPredictiveRetrosynthesisEnabled();

export const isPredictiveRetrosynthesisEnabled = () =>
  (isPaiPredictiveRetrosynthesisEnabled() || isIktosPredictiveRetrosynthesisEnabled()) && !AuthHelper.isAnonymousUser();

export const getPredictivePartner = () => {
  if (isIktosPredictiveRetrosynthesisEnabled()) return ProjectSynthesisPartner.IKTOS;
  if (isPaiPredictiveRetrosynthesisEnabled()) return ProjectSynthesisPartner.PAI;
  return null;
};

// need to remove unique ids from smiles objects before sending to BE
export const updateParamsForBE = (projectParams, optionsResponse) => {
  let mappedParams = projectParams;
  if (projectParams?.predictedParams?.partner === ProjectSynthesisPartner.IKTOS) {
    // update maxShippingTime and maxPricePerGram depending upon if RCS is checked or not
    const { usedBuildingBlocks, maxPricePerGram, isRCSUnchecked, firstDisconnect, maxNoOfSteps } = projectParams.predictedParams;
    const updatedMaxNoOfSteps = firstDisconnect ? 1 : maxNoOfSteps;
    const updatedUsedBuildingBlocks = isRCSUnchecked
      ? usedBuildingBlocks.filter((block) => [RCS10D, RCSANY].indexOf(block) < 0)
      : [...usedBuildingBlocks];
    const updatedMaxPricePerGram = isRCSUnchecked ? '' : maxPricePerGram;
    // map smiles objects to pure string array without uniq ids
    mappedParams = {
      ...projectParams,
      predictedParams: {
        ...projectParams.predictedParams,
        usedBuildingBlocks: [...updatedUsedBuildingBlocks],
        maxNoOfSteps: updatedMaxNoOfSteps,
        maxPricePerGram: updatedMaxPricePerGram,
        includeSubstructures: projectParams.predictedParams.includeSubstructures.map((s) => s.value),
        excludeSubstructures: projectParams.predictedParams.excludeSubstructures.map((s) => s.value),
      },
    };

    // Map RCS building blocks to RPS building blocks if RPS is available
    if (isRPSAvailableFn(optionsResponse)) {
      const isRCSChecked = !isRCSUnchecked;
      const isRPSChecked = projectParams?.predictedParams?.isRPSChecked;
      // eslint-disable-next-line custom/no-unsafe-deep-access
      mappedParams.predictedParams.usedBuildingBlocks = mapUsedRCSToRPSBuildingBlockLibrariesIktos(
        projectParams?.predictedParams?.usedBuildingBlocks,
        isRCSChecked && isRPSChecked,
        optionsResponse
      );
    }
  } else if (projectParams?.predictedParams?.partner === ProjectSynthesisPartner.PAI) {
    // Map RCS building blocks to RPS building blocks if RPS is available
    if (isRPSAvailableFn(optionsResponse)) {
      const isRCSChecked = isRCSCheckedFn(projectParams?.predictedParams?.usedBuildingBlocks);
      const isRPSChecked = projectParams?.predictedParams?.isRPSChecked;
      // eslint-disable-next-line custom/no-unsafe-deep-access
      mappedParams.predictedParams.usedBuildingBlocks = mapUsedRCSToRPSBuildingBlockLibrariesPai(
        projectParams?.predictedParams?.usedBuildingBlocks,
        isRCSChecked && isRPSChecked,
        optionsResponse
      );
    }
  }

  mappedParams.enableNoResultsExtension = true; // No results param
  return mappedParams;
};

// need to populate unique ids for react components to work correctly
export const updatePredictedParamsFromBE = (predictedParams) => {
  let mappedParams = predictedParams;
  if (predictedParams && predictedParams.partner === ProjectSynthesisPartner.IKTOS) {
    const { usedBuildingBlocks = [], maxPricePerGram, maxNoOfSteps } = predictedParams;
    const isRCSUnchecked = !usedBuildingBlocks || !usedBuildingBlocks.some((block) => [RCS10D, RCSANY].includes(block));
    const updatedMaxPricePerGram = !isRCSUnchecked && maxPricePerGram ? maxPricePerGram.toString() : '';
    // map smiles objects to pure string array without uniq ids
    mappedParams = {
      ...predictedParams,
      isRCSUnchecked,
      usedBuildingBlocks: usedBuildingBlocks || [],
      firstDisconnect: maxNoOfSteps === 1,
      maxPricePerGram: updatedMaxPricePerGram,
      includeSubstructures: predictedParams.includeSubstructures
        ? predictedParams.includeSubstructures.map((s) => ({ id: uuid(), value: s }))
        : [],
      excludeSubstructures: predictedParams.excludeSubstructures
        ? predictedParams.excludeSubstructures.map((s) => ({ id: uuid(), value: s }))
        : [],
    };
  }
  return mappedParams;
};
